import React, { useState } from 'react'
import { Button, Grid, Typography, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { connect } from 'react-redux'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import MultiChoiceNodeQuestion from './MultiChoiceNodeQuestion'
import {
    ScenarioInteractiveElementType,
} from '../../../../Logic/ScenarioConstants'
import ReactPlayer from 'react-player/lazy'

const useStyles = makeStyles((theme) => ({
    textFields: {
        width: '380px',
        textColor: 'white',
    },
    nodeTitle: {
        fontFamily: 'Lato',
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '30px',
        lineHeight: '22px',
        paddingBottom: '8px',
        color: '#35323C',
    },
    scenario: {
        paddingTop: '30px',
    },
    scenarioDescriptionText: {
        color: '#8e8e93',
        fontFamily: 'Lato',
        fontStyle: 'normal',
        fontWeight: 500,
    },
    videoUrlInput: {
        width: '100%',
        marginTop: '10px',
    },
    videoPreviewHint: {
        marginTop: '4px',
    },
    validUrlIndicator: {
        fontSize: 12,
        fontFamily: 'Lato',
        marginTop: '4px',
    },
}))

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
}

const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
    background: isDragging && 'lightgreen',
    ...draggableStyle,
})

const VideoNodeModal = (props) => {
    const classes = useStyles()
    const [shouldMinimise, setShouldMinimise] = useState(false)

    const videoOption = props.node?.testOptions?.find(
        (to) => to.scenarioInteractiveElementType === ScenarioInteractiveElementType.VideoInteraction
    )
    const videoUrl = videoOption?.videoUrl || ''
    const isValidUrl = videoUrl && ReactPlayer.canPlay(videoUrl)

    const onDragEnd = (result) => {
        if (!result.destination) {
            return
        }
        const itemsNew = reorder(props.node.testOptions, result.source.index, result.destination.index)
        props.onReorderTestOptions(itemsNew)
    }

    const handleOnNodeInteractionScoringChanged = (testOptionIndex, shouldBeScored) => {
        if (!props.canEditLesson) {
            return
        }
        props.handleOnNodeInteractionScoringChanged(testOptionIndex, shouldBeScored)
    }

    const handleVideoUrlChange = (e) => {
        const newUrl = e.target.value
        if (props.onVideoUrlChanged) {
            props.onVideoUrlChanged(newUrl)
        }
    }

    const videoIntroduction = () => {
        return (
            <React.Fragment>
                <Grid item xs={12} sm={12} md={12} style={{ marginTop: '80px' }}>
                    <Typography
                        variant="h6"
                        style={{
                            boxShadow: 'none',
                            color: 'black',
                            fontFamily: 'Lato',
                            fontSize: 32,
                            fontWeight: 'bold',
                        }}>
                        Video Stage
                    </Typography>
                    <Typography
                        style={{
                            boxShadow: 'none',
                            letterSpacing: '0.02em',
                            fontSize: 14,
                            color: '#8E8E93',
                            fontFamily: 'Lato',
                        }}>
                        Add a video for your learners to watch. Paste any YouTube, Vimeo, or direct video URL below.
                        Optionally, add quiz questions about the video content.
                    </Typography>
                </Grid>
            </React.Fragment>
        )
    }

    const videoUrlSection = () => {
        return (
            <Grid container style={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Typography
                        variant="h6"
                        style={{
                            boxShadow: 'none',
                            color: 'black',
                            fontFamily: 'Lato',
                            fontSize: 22,
                            fontWeight: 'bold',
                        }}>
                        Video URL
                    </Typography>
                    <TextField
                        className={classes.videoUrlInput}
                        variant="outlined"
                        size="small"
                        placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                        value={videoUrl}
                        onChange={handleVideoUrlChange}
                        disabled={!props.canEditLesson}
                    />
                    {videoUrl && (
                        <Typography
                            className={classes.validUrlIndicator}
                            style={{ color: isValidUrl ? '#4CAF50' : '#F44336' }}>
                            {isValidUrl ? 'Valid video URL detected' : 'URL not recognized as a supported video source'}
                        </Typography>
                    )}
                </Grid>
                {isValidUrl && (
                    <Typography
                        style={{
                            fontSize: 12,
                            fontFamily: 'Lato',
                            color: '#8E8E93',
                            marginTop: '4px',
                        }}>
                        Video preview is shown in the phone on the left.
                    </Typography>
                )}
            </Grid>
        )
    }

    const multiChoiceQuestions = (props.node?.testOptions || []).filter(
        (to) => to.scenarioInteractiveElementType === ScenarioInteractiveElementType.MultiChoice
    )
    const hasQuestions = multiChoiceQuestions.length > 0

    return (
        <Grid item xs={7} sm={7} md={7} style={{ minWidth: '528px' }}>
            {videoIntroduction()}
            {videoUrlSection()}

            <Grid container>
                <Grid item xs={8} sm={8} md={8} className={classes.scenario}>
                    <Typography
                        className={classes.nodeTitle}
                        style={{
                            boxShadow: 'none',
                            color: 'black',
                            fontFamily: 'Lato',
                            fontSize: 22,
                            fontWeight: 'bold',
                            marginTop: '20px',
                        }}>
                        Quiz Questions (Optional)
                    </Typography>
                    <Typography
                        style={{
                            boxShadow: 'none',
                            letterSpacing: '0.02em',
                            fontSize: 14,
                            color: '#8E8E93',
                            fontFamily: 'Lato',
                            marginBottom: '20px',
                        }}>
                        Optionally add quiz questions about the video. The order of each answer box is randomised in the
                        app.
                    </Typography>
                </Grid>
                {hasQuestions && (
                    <Grid item xs={4} sm={4} md={4} className={classes.scenario}>
                        <FormControlLabel
                            control={
                                <Switch checked={shouldMinimise} onChange={() => setShouldMinimise(!shouldMinimise)} />
                            }
                            label="Compact view"
                        />
                    </Grid>
                )}
            </Grid>

            {hasQuestions && (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {props.node.testOptions.map(
                                    (testOption, index) =>
                                        testOption.scenarioInteractiveElementType ===
                                            ScenarioInteractiveElementType.MultiChoice && (
                                            <Draggable
                                                key={testOption.testOptionId}
                                                draggableId={testOption.testOptionId}
                                                index={index}
                                                draggable={!props.canEditLesson}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style
                                                        )}>
                                                        <MultiChoiceNodeQuestion
                                                            onTestOptionTextLanguageTypeChange={
                                                                props.onTestOptionTextLanguageTypeChange
                                                            }
                                                            handleMultiChoiceQuestionDeleted={
                                                                props.handleMultiChoiceQuestionDeleted
                                                            }
                                                            handleUpdateMultiChoiceQuestionChanged={
                                                                props.handleUpdateMultiChoiceQuestionChanged
                                                            }
                                                            handleUpdateMultiChoiceQuestionL2Changed={
                                                                props.handleUpdateMultiChoiceQuestionL2Changed
                                                            }
                                                            handleUpdateMultiChoiceAlternativeChanged={
                                                                props.handleUpdateMultiChoiceAlternativeChanged
                                                            }
                                                            languageId={props.languageId}
                                                            testOption={testOption}
                                                            index={index}
                                                            node={props.node}
                                                            canEditLesson={props.canEditLesson}
                                                            shouldMinimise={shouldMinimise}
                                                            onNodeInteractionScoringChanged={
                                                                handleOnNodeInteractionScoringChanged
                                                            }
                                                            l1Id={props.l1Id}
                                                            onSetQuizQuestionInFocus={props.onSetQuizQuestionInFocus}
                                                            lessonMode={props.lessonMode}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        )
                                )}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            <Grid
                item
                xs={12}
                sm={12}
                md={12}
                style={{
                    display: 'flex',
                    textAlign: 'center',
                    alignItems: 'center',
                    marginTop: '20px',
                    justifyContent: 'left',
                }}>
                {props.canEditLesson && (
                    <Button
                        onClick={() => props.onAddMultiChoiceQuestion()}
                        style={{
                            backgroundColor: '#143349',
                            fontColor: 'white',
                            color: 'white',
                            marginRight: '5px',
                        }}
                        variant="outlined">
                        Add Question
                    </Button>
                )}
            </Grid>

            {props.node?.validationErrors?.length > 0 && props.displayErrorsSection()}
            {props.saveOrCancelStage()}
        </Grid>
    )
}

VideoNodeModal.propTypes = {}

const mapToProps = (state) => {
    return {
        metadata: state.metadata,
        organization: state.organization,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {}
}

export default connect(mapToProps, mapDispatchToProps)(VideoNodeModal)
